import React, { FC } from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action, ActionSettings } from 'models/action';
import { Category, SubCategory, MaterialGroup } from 'models/organization';
import { RestorationType } from 'models/order';
import RestorationTypeAutocomplete from 'components/shared/restoration-type-autocomplete/restoration-type-autocomplete.component';

type ActionLeftSectionProps = {
    action: Action;
    categories: Category[];
    materials: MaterialGroup[];
    subCategoriesForAction: SubCategory[];
    selectedSubCategories: SubCategory[];
    subCategoryMenuAnchor: null | HTMLElement;
    setSubCategoryMenuAnchor: (anchor: null | HTMLElement) => void;
    handleInputChange: (field: keyof Action, value: any) => void;
    handleMainCategoryChange: (categoryId: string) => void;
    handleSubCategoryChange: (subCategory: SubCategory, selected: boolean) => void;
    handleSettingsChange: (settingKey: keyof ActionSettings, field: string, value: any) => void;
    handleTeethOptionChange: (value: string) => void;
    handleVatChange: (value: string) => void;
    handleDenthubRestorationTypeChange: (value: RestorationType) => void;
};

const ActionLeftSection: FC<ActionLeftSectionProps> = ({
    action,
    categories,
    materials,
    subCategoriesForAction,
    selectedSubCategories,
    subCategoryMenuAnchor,
    setSubCategoryMenuAnchor,
    handleInputChange,
    handleMainCategoryChange,
    handleSubCategoryChange,
    handleSettingsChange,
    handleTeethOptionChange,
    handleVatChange,
    handleDenthubRestorationTypeChange,
}) => {
    const { t: translate } = useTranslation(['client-directory', 'common', 'orders', 'manage-actions']);

    return (
        <Grid item xs={12} md={6} sx={{ mt: 1 }}>
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            fullWidth
                            label={translate('manage-actions:NUMBER')}
                            value={action.number}
                            onChange={(e) => handleInputChange('number', e.target.value)}
                            required
                            sx={{
                                '& .MuiInputLabel-asterisk': {
                                    color: 'red',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={9}>
                        <TextField
                            fullWidth
                            label={translate('common:NAME')}
                            value={action.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                            sx={{
                                '& .MuiInputLabel-asterisk': {
                                    color: 'red',
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={action.isNotesEnabled || false}
                            onChange={(e) => handleInputChange('isNotesEnabled', e.target.checked)}
                        />
                    }
                    label={translate('manage-actions:SHOW_NOTES')}
                />
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={translate('manage-actions:ENTER_NOTES')}
                    value={action.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    disabled={!action.isNotesEnabled}
                    sx={{ mt: 1 }}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControl required fullWidth sx={{ mb: 2 }}>
                    <InputLabel
                        id="main-category-select-label"
                        sx={{
                            '& .MuiFormLabel-asterisk': {
                                color: 'red',
                            },
                        }}
                    >
                        {translate('client-directory:MAIN_CATEGORY')}
                    </InputLabel>
                    <Select
                        id="main-category-select"
                        labelId="main-category-select-label"
                        label={translate('client-directory:MAIN_CATEGORY')}
                        value={action?.mainCategory?._id || ''}
                        onChange={(e) => handleMainCategoryChange(e.target.value)}
                    >
                        {categories.map((category) => (
                            <MenuItem key={category._id} value={category._id}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                        label={translate('client-directory:SUB_CATEGORY')}
                        value={`${selectedSubCategories.length} ${translate('manage-actions:SELECTED')}`}
                        onClick={(e) => setSubCategoryMenuAnchor(e.currentTarget)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <ArrowDownIcon />,
                        }}
                        required
                        sx={{
                            '& .MuiInputLabel-asterisk': {
                                color: 'red',
                            },
                        }}
                    />
                    <Menu
                        anchorEl={subCategoryMenuAnchor}
                        open={Boolean(subCategoryMenuAnchor)}
                        onClose={() => setSubCategoryMenuAnchor(null)}
                        slotProps={{
                            paper: {
                                sx: {
                                    maxHeight: 300,
                                    width: 300,
                                },
                            },
                        }}
                    >
                        {subCategoriesForAction.map((subCategory) => (
                            <MenuItem key={subCategory._id}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={subCategory.selected || false}
                                            onChange={(e) => handleSubCategoryChange(subCategory, e.target.checked)}
                                        />
                                    }
                                    label={subCategory.name}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{translate('client-directory:MATERIAL_GROUP')}</InputLabel>
                    <Select
                        id="mat-group-select"
                        label={translate('client-directory:MATERIAL_GROUP')}
                        value={action?.materialGroup?._id || ''}
                        onChange={(e) => handleInputChange('materialGroup', e.target.value)}
                    >
                        <MenuItem value="">{translate('orders:NONE')}</MenuItem>
                        {materials.map((material) => (
                            <MenuItem key={material._id} value={material._id}>
                                {material.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {action.materialGroup && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={action.settings?.material?.mandatory || false}
                                onChange={(e) => handleSettingsChange('material', 'mandatory', e.target.checked)}
                            />
                        }
                        label={translate('orders:MANDATORY')}
                    />
                )}
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{translate('manage-actions:CHOOSE_HOW_TO_SELECT_TEETH')}</InputLabel>
                    <Select
                        id="select-teeth"
                        label={translate('manage-actions:CHOOSE_HOW_TO_SELECT_TEETH')}
                        value={action.teethOption || ''}
                        onChange={(e) => handleTeethOptionChange(e.target.value)}
                    >
                        <MenuItem value="Single tooth">{translate('manage-actions:SINGLE_TOOTH')}</MenuItem>
                        <MenuItem value="Range">{translate('orders:RANGE')}</MenuItem>
                        <MenuItem value="Up/low jaw">{translate('manage-actions:UP_LOW_JAW')}</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={action.costPerTooth || false}
                            onChange={(e) => handleInputChange('costPerTooth', e.target.checked)}
                            disabled={
                                action.teethOption === 'Range' ||
                                action.settings?.numberOfUnits?.mandatory ||
                                action.settings?.numberOfUnits?.include
                            }
                        />
                    }
                    label={translate('manage-actions:ACTION_COST_PER_TOOTH')}
                />

                <TextField
                    fullWidth
                    label={translate('manage-actions:ENTER_THE_VAT')}
                    value={action.vat || ''}
                    onChange={(e) => handleVatChange(e.target.value)}
                    sx={{ mt: 2 }}
                />

                <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
                    <RestorationTypeAutocomplete
                        value={action.denthubRestorationType}
                        onChange={handleDenthubRestorationTypeChange}
                        error={!action.denthubRestorationType}
                        helperText={
                            !action.denthubRestorationType ? translate('common:VALIDATION.FIELD_REQUIRED') : undefined
                        }
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={action.quotable || false}
                            onChange={(e) => handleInputChange('quotable', e.target.checked)}
                        />
                    }
                    label={translate('common:QUOTABLE')}
                />
            </Box>
        </Grid>
    );
};
export default ActionLeftSection;
